import React, { useState, useEffect } from 'react';
import { X, Plus } from 'lucide-react';
import { toast } from '../V2Components';
import PaintService from '../../../services/PaintService';

const EMPTY_FORM = {
    paint_id: '',
    surface: '',
    date_painted: '',
    notes: '',
};

/**
 * Modal for attaching a paint to a room OR editing an existing room↔paint
 * junction row's metadata (surface, date_painted, notes).
 *
 * Two modes:
 *   - Create (roomPaint=null): paint picker + surface + date + notes.
 *     Includes a "Create new paint" affordance — clicking it asks the parent
 *     to open PaintFormModal so the user can build a paint without losing
 *     their place in the attach flow.
 *   - Edit (roomPaint=Object): paint picker is disabled. To swap paints,
 *     the user must detach and re-attach (intentional friction — swapping
 *     paints is rare and shouldn't be a one-click operation).
 *
 * The "Edit how it's applied here" entry point in RoomDetails opens this in
 * edit mode. The "Edit shared paint" entry opens PaintFormModal instead.
 *
 * Props:
 *   isOpen      - boolean
 *   onClose     - () => void
 *   onSave      - (payload) => Promise<void>  — parent owns the API call
 *   roomPaint   - existing RoomPaint row for edit mode, null for create
 *   roomId      - the room being edited; a change resets the form
 *   saving      - boolean
 *   onCreatePaint - () => void  — invoked when user clicks "Create new paint"
 *                                 in create mode. Parent should close this
 *                                 modal and open PaintFormModal.
 */
const RoomPaintModal = ({ isOpen, onClose, onSave, roomPaint, roomId, saving, onCreatePaint }) => {
    const [formData, setFormData] = useState(EMPTY_FORM);
    const [paints, setPaints] = useState([]);
    const [loadingPaints, setLoadingPaints] = useState(false);

    // Refetch the paint library every time the modal opens. This is cheaper than
    // wiring a "paints changed" event bus and avoids stale-pick bugs when the
    // user creates a new paint mid-flow.
    useEffect(() => {
        if (!isOpen) return;

        let cancelled = false;
        const load = async () => {
            setLoadingPaints(true);
            try {
                const { paints: list } = await PaintService.listPaints({ limit: 100 });
                if (!cancelled) setPaints(list || []);
            } catch (err) {
                if (!cancelled) {
                    toast.error(err.message || 'Failed to load paint library.');
                    setPaints([]);
                }
            } finally {
                if (!cancelled) setLoadingPaints(false);
            }
        };
        load();
        return () => { cancelled = true; };
    }, [isOpen]);

    // Reset on every (re)open DURING RENDER, not in a useEffect. The component
    // stays mounted while closed (it returns null), so formData still holds the
    // last submission; an effect-based reset commits those stale values to the
    // DOM first and clears them a tick later, losing any input that lands in
    // between (see CircuitForm). Setting state while rendering makes React
    // re-render before committing, so stale values never reach the DOM. The key
    // carries paint_id because the attach-after-create seed has no id, and
    // roomId because RoomDetails doesn't remount when its route id changes, so
    // the modal can stay open across a back/forward navigation.
    const openKey = isOpen ? `${roomId ?? ''}:${roomPaint?.id ?? 'new'}:${roomPaint?.paint_id ?? ''}` : null;
    const [prevOpenKey, setPrevOpenKey] = useState(null);
    if (openKey !== prevOpenKey) {
        setPrevOpenKey(openKey);
        if (openKey !== null) {
            if (roomPaint) {
                setFormData({
                    paint_id: roomPaint.paint_id || roomPaint.paint?.id || '',
                    surface: roomPaint.surface || '',
                    date_painted: roomPaint.date_painted ? roomPaint.date_painted.split('T')[0] : '',
                    notes: roomPaint.notes || '',
                });
            } else {
                setFormData(EMPTY_FORM);
            }
        }
    }

    if (!isOpen) return null;

    // Edit mode = the junction row already exists in the DB (has an `id`).
    // The `roomPaint` prop is overloaded — it carries either:
    //   (a) a full RoomPaint row from the API (has `id`, `paint_id`, embedded paint) → edit
    //   (b) a seed object from the create-after-attach bridge in RoomDetails,
    //       shape `{ paint_id, paint }` with NO `id` → create-with-prefilled-paint
    // Using `!!roomPaint` alone treats (b) as edit, which disables paint_id in
    // the submit payload and triggers a 422 from the backend. Checking for
    // `id` specifically gives us the right disambiguation.
    const isEdit = !!(roomPaint && roomPaint.id);

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (!formData.surface.trim()) {
            toast.error('Surface is required.');
            return;
        }
        if (!isEdit && !formData.paint_id) {
            toast.error('Pick a paint from your library, or create a new one.');
            return;
        }

        const payload = {
            surface: formData.surface.trim(),
            date_painted: formData.date_painted || null,
            notes: formData.notes.trim() || null,
        };

        // paint_id is only sent on create — the junction's paint cannot be swapped
        // via PUT (the backend ignores it anyway, but we keep the wire small).
        if (!isEdit) {
            payload.paint_id = Number(formData.paint_id);
        }

        onSave(payload);
    };

    const labelClass = 'block text-xs font-semibold text-[var(--text-secondary)] uppercase mb-1 theme-transition';
    const inputClass = 'w-full p-3 rounded-lg bg-[var(--bg-hover)] border border-[var(--border)] text-[var(--text-main)] placeholder-[var(--text-muted)] focus:ring-2 focus:ring-[var(--primary)] focus:outline-none theme-transition';

    return (
        <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="room-paint-modal-title"
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
        >
            <div className="bg-[var(--bg-card)] w-full max-w-md rounded-2xl shadow-xl p-6 relative theme-transition max-h-[90vh] overflow-y-auto">
                <button
                    type="button"
                    aria-label="Close dialog"
                    onClick={onClose}
                    className="absolute top-4 right-4 text-[var(--text-muted)] hover:text-[var(--text-main)] theme-transition"
                >
                    <X size={20} />
                </button>

                <h3 id="room-paint-modal-title" className="text-xl font-bold text-[var(--text-main)] mb-6 theme-transition">
                    {isEdit ? 'Edit How Applied' : 'Add Paint to Room'}
                </h3>

                <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Paint picker. Disabled in edit mode to push paint-swapping through
                        detach + re-attach (intentional friction; swapping is rare). */}
                    <div>
                        <label className={labelClass}>Paint</label>
                        {isEdit ? (
                            <div className="p-3 rounded-lg bg-[var(--bg-hover)] border border-[var(--border)] text-[var(--text-main)] theme-transition">
                                {roomPaint.paint?.color_name || 'Unknown paint'}
                                {roomPaint.paint?.manufacturer && (
                                    <span className="text-[var(--text-muted)] ml-2">
                                        — {roomPaint.paint.manufacturer}
                                    </span>
                                )}
                            </div>
                        ) : (
                            <div className="space-y-2">
                                <select
                                    value={formData.paint_id}
                                    onChange={(e) => handleChange('paint_id', e.target.value)}
                                    disabled={loadingPaints}
                                    className={inputClass}
                                    required
                                >
                                    <option value="">
                                        {loadingPaints ? 'Loading paints...' : 'Select a paint from your library...'}
                                    </option>
                                    {paints.map(p => (
                                        <option key={p.id} value={p.id}>
                                            {[p.color_name, p.manufacturer, p.sheen]
                                                .filter(Boolean)
                                                .join(' — ')}
                                        </option>
                                    ))}
                                </select>
                                {/* Empty-library hint nudges the user to the "Create new paint"
                                    flow rather than leaving them stuck on a useless dropdown. */}
                                {!loadingPaints && paints.length === 0 && (
                                    <p className="text-xs text-[var(--text-muted)] italic">
                                        Your library is empty. Create a paint to attach it here.
                                    </p>
                                )}
                                {onCreatePaint && (
                                    <button
                                        type="button"
                                        onClick={onCreatePaint}
                                        className="flex items-center gap-2 text-sm text-[var(--primary)] hover:opacity-80"
                                    >
                                        <Plus size={16} />
                                        Create new paint
                                    </button>
                                )}
                            </div>
                        )}
                    </div>

                    <div>
                        <label className={labelClass}>
                            Surface <span className="text-red-500">*</span>
                        </label>
                        <input
                            value={formData.surface}
                            onChange={(e) => handleChange('surface', e.target.value)}
                            placeholder="e.g. Walls, Ceiling, Trim, Accent Wall"
                            className={inputClass}
                            required
                        />
                    </div>

                    <div>
                        <label className={labelClass}>Date Painted</label>
                        <input
                            type="date"
                            value={formData.date_painted}
                            onChange={(e) => handleChange('date_painted', e.target.value)}
                            className={inputClass}
                        />
                    </div>

                    <div>
                        <label className={labelClass}>Notes for this surface</label>
                        <textarea
                            value={formData.notes}
                            onChange={(e) => handleChange('notes', e.target.value)}
                            placeholder="Touch-ups, application details..."
                            rows={2}
                            className={`${inputClass} resize-none`}
                        />
                    </div>

                    <div className="flex space-x-3 pt-2">
                        <button
                            type="button"
                            onClick={onClose}
                            className="flex-1 py-3 text-[var(--text-secondary)] hover:bg-[var(--bg-hover)] rounded-lg transition theme-transition"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={saving || loadingPaints}
                            className="flex-1 py-3 bg-[var(--primary)] text-white rounded-lg font-medium hover:opacity-90 transition disabled:opacity-50"
                        >
                            {saving ? 'Saving...' : (isEdit ? 'Update' : 'Attach')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default RoomPaintModal;
